import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import QureLayout from '../components/qure/QureLayout';
import API from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { Chart, registerables } from 'chart.js';
import './QureDashboard.css';

Chart.register(...registerables);

const QureDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_scheduled: 0,
    completed_experiments: 0,
    upcoming_experiments: 0,
    missed_experiments: 0,
    assigned_projects: 0,
    total_teams: 0
  });
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [upcomingExperiments, setUpcomingExperiments] = useState([]);
  const [orgName, setOrgName] = useState('Organization');
  const [userFullName, setUserFullName] = useState('');
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const initChart = useCallback(() => {
    if (!chartRef.current) return;

    // Destroy existing chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    // Prepare data for the last 6 months
    const labels = [];
    const completedData = [];
    const missedData = [];
    const totalData = [];

    // Generate labels for the last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthYear = date.toISOString().slice(0, 7);
      const monthName = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      labels.push(monthName);

      // Find data for this month
      const monthData = monthlyStats.find(stat => stat.month === monthYear);
      completedData.push(monthData ? parseInt(monthData.completed) : 0);
      missedData.push(monthData ? parseInt(monthData.missed) : 0);
      totalData.push(monthData ? parseInt(monthData.total) : 0);
    }

    chartInstanceRef.current = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Completed Experiments',
          data: completedData,
          borderColor: '#4CAF50',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: '#4CAF50',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }, {
          label: 'Missed Experiments',
          data: missedData,
          borderColor: '#F44336',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: '#F44336',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }, {
          label: 'Total Scheduled',
          data: totalData,
          borderColor: '#17a2b8',
          backgroundColor: 'rgba(23, 162, 184, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: '#17a2b8',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          borderDash: [5, 5]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: '#333',
              font: {
                size: 12,
                weight: '600'
              },
              padding: 20,
              usePointStyle: true
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#17a2b8',
            borderWidth: 1,
            cornerRadius: 10,
            displayColors: true,
            titleFont: {
              size: 14,
              weight: 'bold'
            },
            bodyFont: {
              size: 12
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(23, 162, 184, 0.1)',
              drawBorder: false
            },
            ticks: {
              color: '#666',
              font: {
                size: 11
              },
              stepSize: 1
            }
          },
          x: {
            grid: {
              color: 'rgba(23, 162, 184, 0.1)',
              drawBorder: false
            },
            ticks: {
              color: '#666',
              font: {
                size: 11
              }
            }
          }
        }
      }
    });
  }, [monthlyStats]);

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    if (monthlyStats.length > 0 && chartRef.current) {
      initChart();
    }
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [monthlyStats, initChart]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('Fetching Qure dashboard data...');
      
      // Fetch stats
      const statsRes = await API.get('/qure/dashboard/stats');
      console.log('Stats response:', statsRes.data);
      if (statsRes.data && statsRes.data.ok) {
        const statsData = statsRes.data.stats || {};
        console.log('Setting stats:', statsData);
        setStats({
          total_scheduled: parseInt(statsData.total_scheduled) || 0,
          completed_experiments: parseInt(statsData.completed_experiments) || 0,
          upcoming_experiments: parseInt(statsData.upcoming_experiments) || 0,
          missed_experiments: parseInt(statsData.missed_experiments) || 0,
          assigned_projects: parseInt(statsData.assigned_projects) || 0,
          total_teams: parseInt(statsData.total_teams) || 0
        });
        setOrgName(statsRes.data.org_name || 'Organization');
        setUserFullName(statsRes.data.user_full_name || '');
      } else {
        console.error('Stats response not ok:', statsRes.data);
      }

      // Fetch upcoming experiments
      const upcomingRes = await API.get('/qure/dashboard/upcoming-experiments');
      if (upcomingRes.data && upcomingRes.data.ok) {
        setUpcomingExperiments(upcomingRes.data.experiments || []);
      }

      // Fetch monthly stats
      const monthlyRes = await API.get('/qure/dashboard/monthly-stats');
      if (monthlyRes.data && monthlyRes.data.ok) {
        setMonthlyStats(monthlyRes.data.stats || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error response:', error.response);
      // Set default values on error
      setStats({
        total_scheduled: 0,
        completed_experiments: 0,
        upcoming_experiments: 0,
        missed_experiments: 0,
        assigned_projects: 0,
        total_teams: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const getTodayName = () => {
    return new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCompletionRate = () => {
    if (stats.total_scheduled === 0) return 0;
    return Math.round((stats.completed_experiments / stats.total_scheduled) * 100);
  };

  const getUpcomingThisWeek = () => {
    // Return the upcoming_experiments stat
    return stats.upcoming_experiments;
  };

  if (loading) {
    return (
      <QureLayout>
        <div className="dashboard-container">
          <div className="text-center py-5">
            <div className="qure-loading"></div>
            <p className="mt-3">Loading dashboard...</p>
          </div>
        </div>
      </QureLayout>
    );
  }

  return (
    <QureLayout>
      <div className="dashboard-container">
        {/* Welcome Header */}
        <div className="welcome-header fade-in">
          <div className="welcome-content">
            <h1 className="welcome-title">
              Welcome back, {userFullName || user?.username || 'User'}!
            </h1>
            <p className="welcome-subtitle">Mixed Reality Simulation Dashboard - {orgName}</p>
            <div className="date-info">
              <i className="bi bi-calendar-event"></i>
              {getTodayName()}
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card total fade-in stagger-1">
            <div className="stat-icon">
              <i className="bi bi-list-check"></i>
            </div>
            <div className="stat-number">{stats.total_scheduled}</div>
            <div className="stat-label">Total Scheduled</div>
          </div>
          
          <div className="stat-card completed fade-in stagger-2">
            <div className="stat-icon">
              <i className="bi bi-check-circle"></i>
            </div>
            <div className="stat-number">{stats.completed_experiments}</div>
            <div className="stat-label">Completed</div>
          </div>
          
          <div className="stat-card upcoming fade-in stagger-3">
            <div className="stat-icon">
              <i className="bi bi-clock"></i>
            </div>
            <div className="stat-number">{stats.upcoming_experiments}</div>
            <div className="stat-label">Upcoming</div>
          </div>
          
          <div className="stat-card missed fade-in stagger-4">
            <div className="stat-icon">
              <i className="bi bi-exclamation-triangle"></i>
            </div>
            <div className="stat-number">{stats.missed_experiments}</div>
            <div className="stat-label">Missed</div>
          </div>
          
          <div className="stat-card projects fade-in stagger-5">
            <div className="stat-icon">
              <i className="bi bi-folder"></i>
            </div>
            <div className="stat-number">{stats.assigned_projects}</div>
            <div className="stat-label">Assigned Projects</div>
          </div>
          
          <div className="stat-card teams fade-in stagger-6">
            <div className="stat-icon">
              <i className="bi bi-people"></i>
            </div>
            <div className="stat-number">{stats.total_teams}</div>
            <div className="stat-label">Teams</div>
          </div>
        </div>

        {/* Additional Dashboard Metrics */}
        <div className="metrics-row">
          <div className="metric-card completion-rate">
            <div className="metric-icon">
              <i className="bi bi-percent"></i>
            </div>
            <div className="metric-content">
              <div className="metric-value">{getCompletionRate()}%</div>
              <div className="metric-label">Completion Rate</div>
            </div>
          </div>
          
          <div className="metric-card week-upcoming">
            <div className="metric-icon">
              <i className="bi bi-calendar-week"></i>
            </div>
            <div className="metric-content">
              <div className="metric-value">{getUpcomingThisWeek()}</div>
              <div className="metric-label">This Week</div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="content-grid">
          {/* Left Column - Scheduled Experiments */}
          <div className="left-column">
            <div className="section-card fade-in stagger-2">
              <div className="section-header">
                <h5>
                  <i className="bi bi-calendar3"></i>
                  Scheduled Experiments
                </h5>
                <button 
                  className="detail-btn"
                  onClick={() => navigate('/qure-calendar')}
                >
                  <i className="bi bi-arrow-right-circle"></i>
                  View Details
                </button>
              </div>
              <div className="section-content scheduled-experiments-list">
                {upcomingExperiments.length === 0 ? (
                  <div className="empty-state">
                    <i className="bi bi-calendar-x"></i>
                    <p>No upcoming experiments scheduled</p>
                  </div>
                ) : (
                  <div className="experiments-grid">
                    {upcomingExperiments.map((experiment) => {
                      const scheduledDate = new Date(experiment.scheduled_date);
                      const isToday = scheduledDate.toDateString() === new Date().toDateString();
                      const isTomorrow = scheduledDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
                      
                      let dateLabel = '';
                      if (isToday) {
                        dateLabel = 'Today';
                      } else if (isTomorrow) {
                        dateLabel = 'Tomorrow';
                      } else {
                        dateLabel = scheduledDate.toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        });
                      }

                      const timeStr = experiment.scheduled_time 
                        ? formatTime(experiment.scheduled_time) 
                        : '';

                      return (
                        <div key={experiment.schedule_id} className="experiment-card">
                          <div className="experiment-card-header">
                            <div className="experiment-date-badge">
                              <i className="bi bi-calendar-event"></i>
                              <span>{dateLabel}</span>
                            </div>
                            {timeStr && (
                              <div className="experiment-time">
                                <i className="bi bi-clock"></i>
                                <span>{timeStr}</span>
                              </div>
                            )}
                          </div>
                          <div className="experiment-card-body">
                            <h6 className="experiment-title">
                              {experiment.experiment_title || 'Untitled Experiment'}
                            </h6>
                            {experiment.project_name && (
                              <div className="experiment-project">
                                <i className="bi bi-folder"></i>
                                <span>{experiment.project_name}</span>
                              </div>
                            )}
                            {experiment.experiment_description && (
                              <p className="experiment-description">
                                {experiment.experiment_description.length > 100
                                  ? experiment.experiment_description.substring(0, 100) + '...'
                                  : experiment.experiment_description}
                              </p>
                            )}
                          </div>
                          <div className="experiment-card-footer">
                            <span className={`experiment-status status-${experiment.status || 'pending'}`}>
                              {experiment.status === 'pending' ? 'Upcoming' : experiment.status}
                            </span>
                            <button 
                              className="view-experiment-btn"
                              onClick={() => navigate(`/qure-calendar?schedule_id=${experiment.schedule_id}`)}
                            >
                              View Details
                              <i className="bi bi-arrow-right"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="right-column">
            {/* Performance Analytics */}
            <div className="section-card fade-in stagger-3">
              <div className="section-header">
                <h5>
                  <i className="bi bi-graph-up"></i>
                  Performance Analytics
                </h5>
              </div>
              <div className="section-content">
                <div className="chart-container">
                  <canvas ref={chartRef}></canvas>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="section-card fade-in stagger-4">
              <div className="section-header">
                <h5>
                  <i className="bi bi-lightning"></i>
                  Quick Actions
                </h5>
              </div>
              <div className="section-content quick-actions">
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate('/qure-calendar')}
                >
                  <i className="bi bi-calendar-check"></i>
                  <span>View Calendar</span>
                </button>
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate('/qure-analytics')}
                >
                  <i className="bi bi-graph-up"></i>
                  <span>View Analytics</span>
                </button>
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate('/my-teams')}
                >
                  <i className="bi bi-people"></i>
                  <span>My Teams</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </QureLayout>
  );
};

export default QureDashboard;
